import { useQueryClient } from "@tanstack/react-query";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { listing_apis } from "features/Listings/Listings.constants";
import { OFFER_TARGET_TYPE } from "./constants";
import { mapTriggerType } from "./utils/offerContract";

/**
 * All `host/offer/*` endpoints in one place (Postman: Early Bird & Surge Pricing).
 * Auth rides the shared baseApiClient session — never send tokens from here.
 */

export const OFFER_API_URLS = {
  triggerConfig: "host/offer/trigger/config",
  create: "host/offer/create",
  detail: "host/offer/detail",
  update: "host/offer/update",
  list: "host/offer/list",
  expire: "host/offer/expire",
  delete: "host/offer/delete",
  settings: "host/offer/settings",
};

export const OFFER_QUERY_KEYS = {
  triggerConfig: (uidsKey, targetType) => [
    "offer",
    "trigger-config",
    uidsKey,
    targetType ?? null,
  ],
  detail: (offerUid) => ["offer", "detail", offerUid ?? null],
  settings: (listingUid) => ["offer", "settings", listingUid || null],
};

/**
 * Payment-page targets resolve server-side from target_type alone;
 * offering targets need the selected listing uids joined with commas.
 */
export const buildTriggerConfigParams = ({ listingUids, targetType }) => {
  if (targetType === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE) {
    return { target_type: targetType };
  }
  return { listing_uids: [...(listingUids || [])].join(",") };
};

/**
 * Allowed triggers for the current target. Server is the source of truth for
 * availability, capacity and sync dates; on error callers keep local options.
 */
export const useOfferTriggerConfig = ({
  listingUids,
  targetType,
  enabled = true,
  onError = () => {},
} = {}) => {
  const sortedUids = [...(listingUids || [])].sort();
  const uidsKey = sortedUids.join(",");
  const canFetch =
    enabled &&
    (sortedUids.length > 0 ||
      targetType === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE);
  return useCustomQuery({
    queryKey: OFFER_QUERY_KEYS.triggerConfig(uidsKey, targetType),
    url: OFFER_API_URLS.triggerConfig,
    params: buildTriggerConfigParams({ listingUids: sortedUids, targetType }),
    enabled: canFetch,
    onError,
  });
};

/**
 * Intersect local string trigger options with the server-allowed int set.
 * Many-to-one labels (seats/slots/stock filled → 3) stay correct because each
 * local option maps through mapTriggerType independently.
 */
export const filterTriggersByServer = (localTriggers = [], serverData) => {
  const serverTriggers = serverData?.triggers;
  if (!Array.isArray(serverTriggers) || !serverTriggers.length) {
    return localTriggers;
  }
  const allowed = new Set(serverTriggers.map((t) => t?.trigger_type));
  const filtered = localTriggers.filter((t) => allowed.has(mapTriggerType(t)));
  return filtered.length ? filtered : localTriggers;
};

/** Server sync date for sync-to-start, or null. */
export const getServerSyncDate = (serverData) => {
  const schedule = (serverData?.triggers || []).find(
    (t) => t?.trigger_type === 1
  );
  return schedule?.offering_start_datetime || null;
};

/**
 * Server-allowed threshold units for one local trigger
 * (e.g. count-only when the offering has no capacity set).
 * Null = server said nothing; keep local defaults.
 */
export const getServerThresholdUnits = (serverData, localTrigger) => {
  const item = (serverData?.triggers || []).find(
    (t) => t?.trigger_type === mapTriggerType(localTrigger)
  );
  const units = item?.threshold_units;
  return Array.isArray(units) && units.length ? units : null;
};

/** Full offer detail for SeeMore/edit locks (edit_state, editable_fields). */
export const useOfferDetail = (offerUid, enabled = true) =>
  useCustomQuery({
    queryKey: OFFER_QUERY_KEYS.detail(offerUid),
    url: OFFER_API_URLS.detail,
    params: { offer_uid: offerUid },
    enabled: enabled && !!offerUid,
  });

/**
 * Batch (class-group) options for Batch Wise, from creator listing details:
 * `classes[]` = [{ uuid, timings, slots_filled }]. The uuid is the batch_uuid
 * the backend validates against ClassGroup — never the listing uuid.
 */
export const useBatchOptions = (listingUuid, enabled = true) => {
  const query = useCustomQuery({
    queryKey: ["offer", "listing-batches", listingUuid ?? null],
    url: `${listing_apis.get_listing_details}/${listingUuid || ""}`,
    enabled: enabled && !!listingUuid,
    onError: () => {},
  });
  const classes = query.data?.event?.classes ?? query.data?.classes ?? [];
  return {
    ...query,
    batches: (Array.isArray(classes) ? classes : [])
      .filter((cls) => cls?.uuid)
      .map((cls) => ({
        uuid: cls.uuid,
        label: cls.timings || cls.title || cls.uuid,
        title: cls.timings || cls.title || cls.uuid,
      })),
  };
};

/** Account settings when listingUid is empty, offering-level otherwise. */
export const useOfferSettings = (listingUid, enabled = true) =>
  useCustomQuery({
    queryKey: OFFER_QUERY_KEYS.settings(listingUid),
    url: OFFER_API_URLS.settings,
    params: listingUid ? { listing_uid: listingUid } : {},
    enabled,
  });

const useInvalidateOffers = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["offer"] });
};

/** Covers save-account, override, pause and reset payload shapes. */
export const useSaveOfferSettings = ({ onSuccess, ...rest } = {}) => {
  const invalidateOffers = useInvalidateOffers();
  return useCustomMutation({
    url: OFFER_API_URLS.settings,
    onSuccess: (...args) => {
      invalidateOffers();
      if (onSuccess) onSuccess(...args);
    },
    ...rest,
  });
};

export const useExpireOffer = ({ onSuccess, ...rest } = {}) => {
  const invalidateOffers = useInvalidateOffers();
  return useCustomMutation({
    url: OFFER_API_URLS.expire,
    onSuccess: (...args) => {
      invalidateOffers();
      if (onSuccess) onSuccess(...args);
    },
    ...rest,
  });
};

export const useDeleteOffer = ({ onSuccess, ...rest } = {}) => {
  const invalidateOffers = useInvalidateOffers();
  return useCustomMutation({
    url: OFFER_API_URLS.delete,
    onSuccess: (...args) => {
      invalidateOffers();
      if (onSuccess) onSuccess(...args);
    },
    ...rest,
  });
};
